import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, map, filter, flatten, size, includes } from "lodash";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { fetchGet } from "../../../api/fetch";
import DatePicker from "../../Common/DatePicker";
import AddPicture from "../../Common/AddPicture";
import CustomInput from "../../Common/CustomInput";
import FoldableSection from "../../Common/FoldableSection";
import WalkInOutVerification from "./WalkInOutVerification";
import AddPictureGallery from "../../Common/AddPictureGallery";
import ProfileSearchInput from "../../Common/ProfileSearchInput";
import FormSection from "../../Common/FormSection";
import { CELSIUS_THRESHOLD } from "../../../constants/common";
import { isOverThreshold } from "../../../utils/temperature";
import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  formContainer: {
    padding: "24px 24px 0px 24px",
  },
  formLabel: {
    fontSize: "1em",
    color: `${colors.black87} !important`,
    fontWeight: 500,
  },
  formControlLabel: {
    fontSize: "1em",
    color: colors.black54,
  },
  formControlRoot: {
    height: "30px",
  },
};

const WalkInOutFormExpress = (props) => {
  const {
    classes,
    companies,
    formValues,
    handleChange,
    language,
    onSubmitVerification,
    verification,
    user,
    setDefaultValues,
  } = props;

  const siteOptions = filter(
    map(companies, (c) => ({
      ...c,
      options: filter(
        get(c, ["sites"]),
        (s) =>
          size(
            filter(
              get(s, ["zones"]),
              (z) =>
                z.active &&
                z.is_human_access &&
                (get(props, ["eventGroupType", "id"]) === 4
                  ? z.is_entrance
                  : get(props, ["eventGroupType", "id"]) === 5
                    ? z.is_exit
                    : true)
            )
          ) > 0
      ),
    })),
    (c) => size(c.options) > 0
  );

  React.useEffect(() => {
    initDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initDefaultValues = async () => {
    let defaultValues = {};
    if (!get(formValues, ["site"])) {
      if (get(user, ["assignedSite"])) {
        defaultValues.site = get(user, ["assignedSite"]);
      }
      if (size(siteOptions) === 1) {
        defaultValues.site = get(siteOptions, [0, "id"]);
      }
    }
    if (!get(formValues, ["profile"])) {
      try {
        let p = await fetchGet("profiles/00000000-0000-0000-0000-000000000000");
        defaultValues.profile = p;
      } catch (e) {
        console.log(e);
      }
    }
    setDefaultValues(defaultValues);
  };

  const activeSite = get(
    filter(
      flatten(map(companies, (c) => c.sites)),
      (s) => s.id === get(formValues, ["site"])
    ),
    [0]
  );

  const zones = filter(
    get(activeSite, ["zones"]),
    (z) =>
      z.active &&
      z.is_human_access &&
      (get(props, ["eventGroupType", "id"]) === 4
        ? z.is_entrance
        : get(props, ["eventGroupType", "id"]) === 5
          ? z.is_exit
          : true)
  );

  React.useEffect(() => {
    if (
      !get(formValues, ["zone"]) ||
      !includes(
        map(zones, (z) => z.id),
        get(formValues, ["zone"])
      )
    ) {
      if (size(zones) === 1) {
        handleChange("zone", get(activeSite, ["id"]), get(zones, [0, "id"]));
      } else {
        handleChange("zone", get(activeSite, ["id"]), null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones]);

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        className={classes.formContainer}
      >
        <FormSection
          formValue={[get(formValues, ["site"]) || ""]}
          setFormValue={(id, index, value) => handleChange(id, null, value)}
          title={"site"}
          id={"site"}
          type="select"
          options={{
            required: true,
            options: siteOptions,
            categories: true,
          }}
          style={{ maxWidth: "320px" }}
        />
        <FormSection
          formValue={[get(formValues, ["zone"]) || ""]}
          setFormValue={(id, index, value) =>
            handleChange(id, get(formValues, ["site"]), value)
          }
          title={"zone"}
          id={"zone"}
          type="select"
          options={{
            comboBox: true,
            required: true,
            options: map(zones, (z) => ({
              id: z.id,
              name: z.name,
            })),
          }}
          style={{ maxWidth: "320px" }}
        />
        <Box
          style={{
            maxWidth: "420px",
            backgroundColor: colors.lightGrey,
            padding: "0px 8px 0px",
          }}
        >
          <ProfileSearchInput
            profileType={1}
            title={"humanProfile"}
            selectProfile={(profile) => handleChange("profile", null, profile)}
            selectedProfile={get(formValues, ["profile"])}
            sizes={{
              input: 320,
              inputDropdown: 350,
              profile: 420,
            }}
            required
            express
            minHeight={get(formValues, ["profile"]) ? null : 140}
            noMinHeight={get(formValues, ["profile"]) ? true : false}
            noLabel
            style={{ paddingTop: "22px", marginTop: "-6px" }}
            newProfilePhotos={get(formValues, ["photos"])}
          />
          {get(formValues, ["profile"]) && (
            <Grid
              container
              wrap="nowrap"
              style={{
                maxWidth: "420px",
                minHeight: "76px",
                backgroundColor: colors.lightGrey,
                marginTop: "4px",
              }}
            >
              <Grid item xs>
                <WalkInOutVerification
                  profile={get(formValues, ["profile"])}
                  language={language}
                  onSubmitVerification={onSubmitVerification}
                  verification={verification}
                  noVerificationSubmit
                  companies={[get(activeSite, ["company_id"])]}
                  sites={[get(activeSite, ["id"])]}
                  zones={[get(formValues, ["zone"])]}
                  noConfirmation
                />
              </Grid>
              <Grid
                item
                style={{
                  marginTop: "-6px",
                  height: "100%",
                  padding: "6px 10px",
                  backgroundColor: colors.lightGrey,
                }}
              >
                <Grid container alignItems="center">
                  <Grid item className="flexCenter">
                    <img
                      src={images.temperatureIcon}
                      style={{ marginRight: "10px" }}
                      alt="temperature"
                      height="24px"
                    />
                  </Grid>
                  <Grid item style={{ width: "70px" }}>
                    <CustomInput
                      name={"temperature"}
                      value={get(formValues, ["temperature"]) || ""}
                      onChange={(e) =>
                        handleChange("temperature", null, e.target.value)
                      }
                      multiline={false}
                      rows={null}
                      type={"number"}
                    />
                  </Grid>
                  <Grid item className="text-54" style={{ marginLeft: "5px" }}>
                    °C
                  </Grid>
                  <Grid item className="flexCenter" style={{ width: "26px" }}>
                    {get(formValues, ["temperature"]) && (
                      <img
                        src={
                          isOverThreshold(
                            get(formValues, ["temperature"]),
                            CELSIUS_THRESHOLD
                          )
                            ? images.verificationIconWarning
                            : images.greenCheckmarkWhite
                        }
                        style={{ marginLeft: "10px" }}
                        alt="temperature"
                        width="16px"
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Box>
        <Grid item style={{ marginTop: "20px", marginBottom: "8px" }}>
          <Grid container alignItems="center" style={{ marginBottom: "16px" }}>
            <Grid item className="text-87 bold-500">
              {I18n.get("profiles_captured2")}
            </Grid>
            <Grid item>
              <AddPicture
                id="express_entry_captured"
                pictures={get(formValues, ["photos"])}
                setPictures={(photos) => handleChange("photos", null, photos)}
              />
            </Grid>
          </Grid>
          <AddPictureGallery
            photos={get(formValues, ["photos"])}
            setPhotos={(photos) => handleChange("photos", null, photos)}
          />
        </Grid>
        <FoldableSection
          title={`${I18n.get("site_dateTime")}, ${I18n.get("site_comments")}`}
          defaultOpen={false}
          style={{ textTransform: "none", marginTop: "24px" }}
        >
          <Grid item style={{ width: "320px" }}>
            <FormControl component="fieldset">
              <RadioGroup
                name="date"
                value={get(formValues, ["dateType"])}
                onChange={(e) => handleChange("dateType", null, e.target.value)}
              >
                <FormControlLabel
                  value="current"
                  control={<Radio color="primary" />}
                  label={I18n.get("site_currentDateTime")}
                  classes={{
                    label: classes.formControlLabel,
                    root: classes.formControlRoot,
                  }}
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio color="primary" />}
                  label={I18n.get("site_pastDateTime")}
                  classes={{
                    label: classes.formControlLabel,
                    root: classes.formControlRoot,
                  }}
                />
              </RadioGroup>
            </FormControl>
            {get(formValues, ["dateType"]) === "custom" && (
              <DatePicker
                name="date"
                value={get(formValues, ["date"])}
                onChange={(id, value) => handleChange("date", null, value)}
                width="100%"
              />
            )}
            <Box style={{ marginTop: "20px" }}>
              <FormSection
                formValue={[get(formValues, ["note"]) || ""]}
                setFormValue={handleChange}
                title={"comments"}
                id={"note"}
                type="input"
                options={{
                  multiline: true,
                  rows: 5,
                }}
              />
            </Box>
          </Grid>
        </FoldableSection>
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: get(state, ["user"]),
    companies: get(state, ["user", "companies"]),
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(WalkInOutFormExpress)
);
